/**
 * CWB Master Layout — field mapping helpers.
 *
 * The frontend model + Part 1 contract use the aligned field vocabulary.
 * Placement-OWNED fields:
 *   entity_id, canonical_question_id, sort_order, inclusion, widget_override,
 *   conditional
 * Canonical-question DISPLAY fields (read-only on a placement; the BE joins /
 * nests them onto the placement response):
 *   machine_key, title, display_label, answer_options, widget_type, help_text
 *
 * Real / legacy APIs may return older names (id, key, canonical_key,
 * canonical_entity_id, label, text, options, the old inclusion booleans, and
 * either nested `canonical` or flattened canonical fields). ALL such
 * compatibility handling lives here so components + Vuex only ever deal with
 * the Part 1 contract.
 */
import { normalizeConditional } from "@/helpers/cwbConditional";

export const INCLUSION_ALWAYS_ON = "always_on";
export const INCLUSION_CARRIER_DERIVED = "carrier_derived";
export const INCLUSION_OPTIONS = [
  { value: INCLUSION_ALWAYS_ON, label: "Always on" },
  { value: INCLUSION_CARRIER_DERIVED, label: "Carrier derived" },
];

export function inclusionLabel(value) {
  const match = INCLUSION_OPTIONS.find((o) => o.value === value);
  return match ? match.label : "Always on";
}

function deriveInclusion(raw) {
  if (raw.inclusion === INCLUSION_CARRIER_DERIVED || raw.inclusion === INCLUSION_ALWAYS_ON) {
    return raw.inclusion;
  }
  if (raw.carrier_derived === true) return INCLUSION_CARRIER_DERIVED;
  return INCLUSION_ALWAYS_ON;
}

/**
 * Extract read-only canonical-question display data from a placement response,
 * supporting nested (`raw.canonical` / `raw.canonical_question`) or flattened
 * (legacy) shapes. These are DISPLAY ONLY — never editable placement props.
 */
function extractCanonicalDisplay(raw) {
  const c = raw.canonical || raw.canonical_question || {};
  return {
    machine_key: raw.machine_key ?? c.machine_key ?? raw.canonical_key ?? raw.key ?? "",
    title: raw.title ?? c.title ?? c.text ?? raw.text ?? "",
    display_label: raw.display_label ?? c.display_label ?? c.displayLabel ?? "",
    answer_options: raw.answer_options ?? c.answer_options ?? c.options ?? raw.options ?? [],
    widget_type: raw.widget_type ?? c.widget_type ?? null,
    help_text: raw.help_text ?? c.help_text ?? "",
  };
}

export function mapPlacementFromApi(raw = {}) {
  const c = raw.canonical || raw.canonical_question || {};
  return {
    // placement-owned
    entity_id: raw.entity_id ?? raw.id,
    canonical_question_id: raw.canonical_question_id ?? raw.canonical_entity_id ?? c.entity_id ?? c.id ?? null,
    sort_order: raw.sort_order ?? 0,
    inclusion: deriveInclusion(raw),
    widget_override: raw.widget_override ?? null,
    conditional: normalizeConditional(raw.conditional),
    // canonical display (read-only)
    ...extractCanonicalDisplay(raw),
  };
}

export function mapScreenFromApi(raw = {}) {
  return {
    entity_id: raw.entity_id ?? raw.id,
    machine_key: raw.machine_key ?? raw.key ?? "",
    title: raw.title ?? raw.label ?? "",
    stage_id: raw.stage_id ?? raw.stage_entity_id ?? null,
    sort_order: raw.sort_order ?? 0,
    placements: (raw.placements || []).map(mapPlacementFromApi),
  };
}

export function mapStageFromApi(raw = {}) {
  return {
    entity_id: raw.entity_id ?? raw.id,
    machine_key: raw.machine_key ?? raw.key ?? "",
    title: raw.title ?? raw.label ?? "",
    sort_order: raw.sort_order ?? 0,
    screens: (raw.screens || []).map(mapScreenFromApi),
  };
}

export function mapLayoutFromApi(raw) {
  if (!raw) return null;
  return {
    entity_id: raw.entity_id ?? raw.id ?? "master",
    title: raw.title ?? raw.name ?? "Master Layout",
    version: raw.version ?? 1,
    status: raw.status ?? "draft",
    stages: (raw.stages || []).map(mapStageFromApi),
  };
}

/**
 * Map a canonical question (questionLibrary model, legacy field names) into the
 * aligned vocabulary used by the placement picker & conditional builder.
 */
export function mapCanonicalQuestion(q = {}) {
  return {
    entity_id: q.entity_id ?? q.id,
    machine_key: q.machine_key ?? q.canonicalKey ?? q.canonical_key ?? q.id,
    title: q.title ?? q.text ?? "",
    display_label: q.display_label ?? q.displayLabel ?? "",
    answer_options: q.answer_options ?? q.answerOptions ?? q.options ?? [],
    widget_type: q.widget_type ?? q.widgetType ?? null,
    answer_type: q.answer_type ?? q.answerType ?? "",
    help_text: q.help_text ?? q.helpText ?? "",
    status: q.status,
  };
}
